/**
 * Person-facing season name. Mirrors backend/lsc/season_display.py.
 * Stored season_label values and match ids are never rewritten here.
 *
 * NeonSportz seasonIndex 0 is the first season, shown as Season 1.
 * CFB edition labels are already the season people mean and are left alone.
 */

const STORED_SEASON = /^Season\s+(\d+)$/i;

export function displaySeasonLabel(
  storedLabel?: string | null,
  seasonIndex?: number | null,
  edition?: string | null
): string {
  const editionText = (edition || '').trim().toLowerCase();
  const stored = (storedLabel || '').trim();
  if (editionText.startsWith('cfb')) {
    return stored;
  }
  const explicit = coerceIndex(seasonIndex);
  const index = explicit !== null ? explicit : storedSeasonIndex(stored);
  if (index === null) {
    return stored;
  }
  return `Season ${index + 1}`;
}

/** Person-facing edition name. Blank stays blank — it is not a year. */
export function formatMemberEditionLabel(edition?: string | null): string | null {
  const raw = (edition || '').trim().toLowerCase();
  if (!raw) return null;
  const match = /^(madden|cfb)-(\d{2})$/.exec(raw);
  if (!match) return (edition || '').trim() || null;
  return match[1] === 'cfb' ? `CFB ${match[2]}` : `Madden ${match[2]}`;
}

/**
 * Season text when a row can sit beside a different edition.
 *
 * A null or blank row edition stays the bare 1-based season. Blank is not a
 * wildcard and it is not a guessed year.
 * The league's active edition stays bare ("Season 2").
 * A different known edition is prefixed, edition first: "Madden 26 · Season 4".
 * When activeEdition is omitted, the season stays bare.
 */
export function displaySeasonBesideActiveEdition(
  storedLabel?: string | null,
  rowEdition?: string | null,
  activeEdition?: string | null
): string {
  const season = displaySeasonLabel(storedLabel, null, rowEdition);
  const row = (rowEdition || '').trim().toLowerCase();
  const active = (activeEdition || '').trim().toLowerCase();
  if (!row || !active || row === active) {
    return season;
  }
  const prefix = formatMemberEditionLabel(row);
  if (!prefix) return season;
  return season ? `${prefix} · ${season}` : prefix;
}

function storedSeasonIndex(storedLabel: string): number | null {
  const match = STORED_SEASON.exec(storedLabel);
  if (!match) return null;
  return Number.parseInt(match[1], 10);
}

function coerceIndex(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  if (!Number.isInteger(value) || value < 0) return null;
  return value;
}
